// Inc D — post-FX / cinematic. Pure helpers + shader data, WebGL-free so the
// gating/policy/clamp logic is unit-testable without a GL context. The actual
// EffectComposer/passes live in spark-adapter.ts (the only render point).
//
// Pipeline (Codex design, SPIKE_S1_S2_findings.md §S1):
//   RenderPass -> UnrealBloomPass -> ColorGrade -> Vignette -> OutputPass
// Color/alpha contract (Codex [CRITICAL] A): grading NEVER touches the shared
// WebGLRenderer (toneMapping/outputColorSpace stay at Three defaults so the
// direct + XR paths are byte-identical). All grading is inside composer passes.

export interface BloomOptions {
  readonly strength: number;
  readonly radius: number;
  readonly threshold: number;
}

export interface ColorGradeOptions {
  readonly exposure: number;
  readonly contrast: number;
  readonly saturation: number;
  readonly aces: boolean;
}

export interface VignetteOptions {
  readonly offset: number;
  readonly darkness: number;
}

export interface PostFxOptions {
  readonly bloom: BloomOptions;
  readonly colorGrade: ColorGradeOptions;
  readonly vignette: VignetteOptions;
}

// Conservative "cinematic but faithful" defaults for archviz: subtle bloom on
// highlights, mild filmic grade, soft corner vignette. Tunable + validated in
// real browser before ship (SPIKE_S1 only validated bloom).
export const DEFAULT_POST_FX_OPTIONS: PostFxOptions = Object.freeze({
  bloom: Object.freeze({ strength: 0.2, radius: 0.4, threshold: 0.85 }),
  colorGrade: Object.freeze({ exposure: 1.0, contrast: 0.05, saturation: 1.05, aces: true }),
  // Stock VignetteShader mixes toward vec3(1.0 - darkness): darkness near 1 ⇒
  // target near black (a real darkening vignette). offset = reach (smaller ⇒
  // only the far corners darken). 0.5 would mix toward mid-grey (washes blacks).
  vignette: Object.freeze({ offset: 0.95, darkness: 0.9 }),
});

// Neutral options: composer becomes a no-op (≡ direct render through
// OutputPass). Used by the baseline-vs-composer parity test.
export const NEUTRAL_POST_FX_OPTIONS: PostFxOptions = Object.freeze({
  bloom: Object.freeze({ strength: 0, radius: 0, threshold: 1 }),
  colorGrade: Object.freeze({ exposure: 1, contrast: 0, saturation: 1, aces: false }),
  vignette: Object.freeze({ offset: 1, darkness: 0 }),
});

function clamp(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo;
  return v < lo ? lo : v > hi ? hi : v;
}

// Fill defaults + clamp into safe ranges. Pure.
export function normalizePostFxOptions(opts?: Partial<PostFxOptions>): PostFxOptions {
  const b = opts?.bloom;
  const c = opts?.colorGrade;
  const v = opts?.vignette;
  const d = DEFAULT_POST_FX_OPTIONS;
  return {
    bloom: {
      strength: clamp(b?.strength ?? d.bloom.strength, 0, 3),
      radius: clamp(b?.radius ?? d.bloom.radius, 0, 1),
      threshold: clamp(b?.threshold ?? d.bloom.threshold, 0, 1),
    },
    colorGrade: {
      exposure: clamp(c?.exposure ?? d.colorGrade.exposure, 0, 4),
      contrast: clamp(c?.contrast ?? d.colorGrade.contrast, -1, 1),
      saturation: clamp(c?.saturation ?? d.colorGrade.saturation, 0, 2),
      aces: c?.aces ?? d.colorGrade.aces,
    },
    vignette: {
      offset: clamp(v?.offset ?? d.vignette.offset, 0, 3),
      darkness: clamp(v?.darkness ?? d.vignette.darkness, 0, 1),
    },
  };
}

// Adapter-side render gate (Codex [CRITICAL] F). Post-FX renders only when
// requested, the composer exists, and we are fully outside any XR state
// (active/starting/rolling-back/presenting) — EffectComposer is incompatible
// with WebXRManager's setAnimationLoop.
export interface PostFxRenderGate {
  readonly postFxRequested: boolean;
  readonly hasComposer: boolean;
  readonly xrSessionActive: boolean;
  readonly pendingXRRequest: boolean;
  readonly xrTransitioning: boolean;
  readonly isPresenting: boolean;
}

export function shouldRenderPostFx(g: PostFxRenderGate): boolean {
  return (
    g.postFxRequested &&
    g.hasComposer &&
    !g.xrSessionActive &&
    !g.pendingXRRequest &&
    !g.xrTransitioning &&
    !g.isPresenting
  );
}

// Orchestrator-side policy (Codex [WARNING] G + [WARNING] I). Separates what the
// toggle button reflects (userPreference) from what actually renders
// (effective). The tour forces ON; on coarse-pointer (mobile MVP) the feature
// is unavailable so it never renders and the toggle is shown-disabled.
export interface PostFxPolicyInputs {
  readonly userPreference: boolean;
  readonly tourActive: boolean;
  readonly pointerCoarse: boolean;
}

export interface PostFxSnapshot {
  readonly available: boolean;
  readonly userPreference: boolean;
  readonly effective: boolean;
}

export function resolvePostFxSnapshot(i: PostFxPolicyInputs): PostFxSnapshot {
  const available = !i.pointerCoarse;
  return {
    available,
    userPreference: i.userPreference,
    effective: available && (i.userPreference || i.tourActive),
  };
}

// Per-session persistence of the user's toggle choice (decision: "persiste
// nella sessione"). Safe against disabled/throwing storage (private mode).
export const POST_FX_STORAGE_KEY = 'oniri.viewer.postfx';

export function readPostFxPreference(storage: Storage | null | undefined): boolean {
  if (storage == null) return false;
  try {
    return storage.getItem(POST_FX_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function writePostFxPreference(enabled: boolean, storage: Storage | null | undefined): void {
  if (storage == null) return;
  try {
    storage.setItem(POST_FX_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // ignore (private mode / quota)
  }
}

// Custom color-grade shader (exposure + ACES filmic + contrast + saturation).
// Operates on the working-space buffer BEFORE OutputPass (which applies the
// linear->sRGB conversion). Plain shader object — no THREE import so this file
// stays WebGL-free; spark-adapter.ts wraps it in a ShaderPass.
export const ColorGradeShader = {
  name: 'ColorGradeShader',
  uniforms: {
    tDiffuse: { value: null as unknown },
    exposure: { value: DEFAULT_POST_FX_OPTIONS.colorGrade.exposure },
    contrast: { value: DEFAULT_POST_FX_OPTIONS.colorGrade.contrast },
    saturation: { value: DEFAULT_POST_FX_OPTIONS.colorGrade.saturation },
    acesEnabled: { value: DEFAULT_POST_FX_OPTIONS.colorGrade.aces ? 1 : 0 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float exposure;
    uniform float contrast;
    uniform float saturation;
    uniform float acesEnabled;
    varying vec2 vUv;

    // Narkowicz ACES filmic approximation (scene-linear in/out).
    vec3 acesFilmic( vec3 x ) {
      const float a = 2.51;
      const float b = 0.03;
      const float c = 2.43;
      const float d = 0.59;
      const float e = 0.14;
      return clamp( ( x * ( a * x + b ) ) / ( x * ( c * x + d ) + e ), 0.0, 1.0 );
    }

    void main() {
      vec4 texel = texture2D( tDiffuse, vUv );
      vec3 color = texel.rgb * exposure;
      vec3 toned = acesFilmic( color );
      color = mix( color, toned, acesEnabled );
      color = ( color - 0.5 ) * ( 1.0 + contrast ) + 0.5;
      float luma = dot( color, vec3( 0.2126, 0.7152, 0.0722 ) );
      color = mix( vec3( luma ), color, saturation );
      gl_FragColor = vec4( clamp( color, 0.0, 1.0 ), texel.a );
    }
  `,
};
